import { useEffect } from "react";
import { trpc } from "@/trpc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EventType, TshirtSize, TravelMode } from "@satyrsmc/shared/client";
import type { MemberEventDetail } from "@satyrsmc/shared/dto/member/event";
import { enumerateEventDays, isDayPassAvailable } from "@satyrsmc/shared/lib/event-days";
import { formatWeekdayMonthDay } from "@/lib/date-utils";
import { includesTshirt, travelModesFor } from "@satyrsmc/shared/lib/day-pass-policy";
import { useWizard } from "./WizardContext";

const TSHIRT_SIZE_LABELS: Record<TshirtSize, string> = {
  [TshirtSize.S]: "S",
  [TshirtSize.M]: "M",
  [TshirtSize.L]: "L",
  [TshirtSize.XL]: "XL",
  [TshirtSize.XXL]: "2XL",
  [TshirtSize.XXXL]: "3XL",
};

const TRAVEL_MODE_LABELS: Record<TravelMode, string> = {
  [TravelMode.Motorcycle]: "Motorcycle",
  [TravelMode.CarTruck]: "Car/Truck",
  [TravelMode.RvCamper]: "RV/Camper",
};

interface Props {
  event: MemberEventDetail;
}

function DietaryRestrictionsField() {
  const { state, dispatch } = useWizard();
  const hasDietary = state.dietaryRestrictions !== null;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Dietary restrictions?</p>
      <div className="flex gap-3">
        {[
          { label: "No", value: false },
          { label: "Yes", value: true },
        ].map(({ label, value }) => (
          <button
            key={label}
            type="button"
            className={`rounded-lg border px-4 py-2 text-sm transition-colors ${
              hasDietary === value
                ? "border-primary bg-primary/10 font-medium"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() =>
              dispatch({
                type: "SET_DIETARY_RESTRICTIONS",
                value: value ? "" : null,
              })
            }
          >
            {label}
          </button>
        ))}
      </div>
      {hasDietary && (
        <textarea
          value={state.dietaryRestrictions ?? ""}
          onChange={(e) => dispatch({ type: "SET_DIETARY_RESTRICTIONS", value: e.target.value })}
          placeholder="Please describe your dietary restrictions"
          rows={3}
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
        />
      )}
    </div>
  );
}

function RidesFields() {
  const { state, dispatch } = useWizard();
  const isRider = state.isRider;
  const bikesQuery = trpc.members.bikes.list.useQuery(undefined, { enabled: isRider === true });
  const bikes = bikesQuery.data?.items ?? [];

  // Auto-select primary bike when bikes load
  useEffect(() => {
    if (isRider && state.bikeId === null && bikes.length > 0) {
      const primary = bikes.find((b) => b.is_primary) ?? bikes[0];
      if (primary) dispatch({ type: "SET_BIKE_ID", bikeId: primary.id });
    }
  }, [isRider, state.bikeId, bikes, dispatch]);

  // Clear bike when not a rider
  useEffect(() => {
    if (!isRider && state.bikeId !== null) {
      dispatch({ type: "SET_BIKE_ID", bikeId: null });
    }
  }, [isRider, state.bikeId, dispatch]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">Are you riding or a passenger?</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "Rider", value: true },
            { label: "Passenger", value: false },
          ].map(({ label, value }) => (
            <button
              key={label}
              type="button"
              className={`rounded-lg border p-3 text-sm transition-colors ${
                isRider === value
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border hover:border-primary/50"
              }`}
              onClick={() => dispatch({ type: "SET_IS_RIDER", isRider: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isRider && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Which bike?</p>
          {bikesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : bikes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No bikes in your garage. To add one, visit your{" "}
              <a href="/settings/garage" className="underline">
                profile settings
              </a>
              .
            </p>
          ) : (
            <Select
              value={state.bikeId ?? ""}
              onValueChange={(val) => dispatch({ type: "SET_BIKE_ID", bikeId: val || null })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a bike" />
              </SelectTrigger>
              <SelectContent>
                {bikes.map((bike) => (
                  <SelectItem key={bike.id} value={bike.id}>
                    {bike.year} {bike.make} {bike.model}
                    {bike.trim ? ` ${bike.trim}` : ""}
                    {bike.is_primary ? " (primary)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}

function AttendanceChoiceField({ event }: { event: MemberEventDetail }) {
  const { state, dispatch } = useWizard();
  const attendance = state.attendance;
  const isSingleDay = attendance.kind === "single";
  const days = enumerateEventDays(event.start_date, event.end_date);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">Are you attending…</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: "The full event", single: false },
            { label: "A single day", single: true },
          ].map(({ label, single }) => {
            const selected = single ? attendance.kind === "single" : attendance.kind === "full";
            return (
              <button
                key={label}
                type="button"
                aria-pressed={selected}
                className={`rounded-lg border p-3 text-sm transition-colors ${
                  selected
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() =>
                  dispatch({
                    type: "SET_ATTENDANCE",
                    attendance: single ? { kind: "single", date: null } : { kind: "full" },
                  })
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Only the event's own days are offered, so an out-of-range date is unreachable. */}
      {isSingleDay && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Which day?</p>
          <div className="grid grid-cols-2 gap-2">
            {days.map((day) => (
              <button
                key={day}
                type="button"
                className={`rounded-lg border p-3 text-sm transition-colors ${
                  attendance.date === day
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() =>
                  dispatch({ type: "SET_ATTENDANCE", attendance: { kind: "single", date: day } })
                }
              >
                {formatWeekdayMonthDay(day)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BadgerPioneerFields({ event }: { event: MemberEventDetail }) {
  const { state, dispatch } = useWizard();
  const isDayPass = state.attendance.kind === "single";
  const showBadgerFields = event.event_type === EventType.Badger;
  const showTshirt = showBadgerFields && includesTshirt(isDayPass);
  const showBikePicker = state.campoutDetails?.travelingBy === TravelMode.Motorcycle;

  const bikesQuery = trpc.members.bikes.list.useQuery(undefined, { enabled: showBikePicker });
  const bikes = bikesQuery.data?.items ?? [];

  useEffect(() => {
    if (!state.campoutDetails) {
      dispatch({
        type: "SET_CAMPOUT_DETAILS",
        details: { tshirtSize: null, travelingBy: null, club: "" },
      });
    }
  }, [state.campoutDetails, dispatch]);

  useEffect(() => {
    if (showBikePicker && state.bikeId === null && bikes.length > 0) {
      const primary = bikes.find((b) => b.is_primary) ?? bikes[0];
      if (primary) dispatch({ type: "SET_BIKE_ID", bikeId: primary.id });
    }
  }, [showBikePicker, state.bikeId, bikes, dispatch]);

  useEffect(() => {
    if (!showBikePicker && state.bikeId !== null) {
      dispatch({ type: "SET_BIKE_ID", bikeId: null });
    }
  }, [showBikePicker, state.bikeId, dispatch]);

  const details = state.campoutDetails;
  const setDetails = (patch: Partial<typeof details>) =>
    dispatch({
      type: "SET_CAMPOUT_DETAILS",
      details: { ...(details ?? { tshirtSize: null, travelingBy: null, club: "" }), ...patch },
    });

  return (
    <div className="space-y-6">
      {isDayPassAvailable(event) && <AttendanceChoiceField event={event} />}

      {showTshirt && (
        <div className="space-y-2">
          <p className="text-sm font-medium">T-Shirt Size</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.values(TshirtSize).map((size) => (
              <button
                key={size}
                type="button"
                className={`rounded-lg border p-2 text-sm transition-colors ${
                  details?.tshirtSize === size
                    ? "border-primary bg-primary/10 font-medium"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => setDetails({ tshirtSize: size })}
              >
                {TSHIRT_SIZE_LABELS[size]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium">How are you traveling?</p>
        <div className="grid grid-cols-1 gap-2">
          {travelModesFor(isDayPass).map((mode) => (
            <button
              key={mode}
              type="button"
              className={`rounded-lg border p-3 text-sm text-left transition-colors ${
                details?.travelingBy === mode
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border hover:border-primary/50"
              }`}
              onClick={() => setDetails({ travelingBy: mode })}
            >
              {TRAVEL_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      {showBikePicker && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Which bike?</p>
          {bikes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No bikes in your garage. To add one, visit your{" "}
              <a href="/settings/garage" className="underline">
                profile settings
              </a>
              .
            </p>
          ) : (
            <Select
              value={state.bikeId ?? ""}
              onValueChange={(val) => dispatch({ type: "SET_BIKE_ID", bikeId: val || null })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a bike" />
              </SelectTrigger>
              <SelectContent>
                {bikes.map((bike) => (
                  <SelectItem key={bike.id} value={bike.id}>
                    {bike.year} {bike.make} {bike.model}
                    {bike.trim ? ` ${bike.trim}` : ""}
                    {bike.is_primary ? " (primary)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {showBadgerFields && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            Club <span className="font-normal text-muted-foreground">(optional)</span>
          </p>
          <input
            type="text"
            value={details?.club ?? ""}
            onChange={(e) => setDetails({ club: e.target.value })}
            placeholder="Your motorcycle club"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
      )}

      <DietaryRestrictionsField />
    </div>
  );
}

export function Step1Details({ event }: Props) {
  if (event.event_type === EventType.Rides) {
    return <RidesFields />;
  }
  if (event.event_type === EventType.Badger || event.event_type === EventType.PioneerRun) {
    return <BadgerPioneerFields event={event} />;
  }
  // Social and other event types — no additional fields
  return null;
}
